import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { flushQueue, pendingCount } from "@/lib/auditStore";

const SyncContext = createContext(null);

// Stato di connessione e sincronizzazione condiviso da tutte le schermate dell'auditor.
export function SyncProvider({ children }) {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [error, setError] = useState(null);
  const busy = useRef(false);
  const listeners = useRef(new Set());

  const refresh = useCallback(async () => {
    try { setPending(await pendingCount()); } catch { /* archivio non disponibile */ }
  }, []);

  const sync = useCallback(async () => {
    if (busy.current) return null;
    busy.current = true;
    setSyncing(true);
    let res = null;
    try {
      res = await flushQueue();
      setError(res.ok ? null : res.error);
      if (res.ok) setLastSync(new Date());
      if (res.conflicts.length) toast.warning(`${res.conflicts.length} risposta/e modificate anche altrove: vale la più recente. Controlla le voci evidenziate.`);
      if (res.rejected.length) toast.error(`${res.rejected.length} elemento/i non accettati dal server (${res.rejected[0].why}).`);
      listeners.current.forEach((fn) => fn(res));
    } finally {
      busy.current = false;
      setSyncing(false);
      await refresh();
    }
    return res;
  }, [refresh]);

  useEffect(() => {
    const on = () => { setOnline(true); sync(); };
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    refresh();
    sync();
    const timer = setInterval(() => { if (navigator.onLine) sync(); }, 20000);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); clearInterval(timer); };
  }, [sync, refresh]);

  const subscribe = useCallback((fn) => { listeners.current.add(fn); return () => listeners.current.delete(fn); }, []);
  const value = useMemo(() => ({ online, pending, syncing, lastSync, error, sync, refresh, subscribe }), [online, pending, syncing, lastSync, error, sync, refresh, subscribe]);
  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>;
}

export const useSync = () => useContext(SyncContext);

// Indicatore chiaro: "salvato sul dispositivo" oppure "sincronizzato".
export function SyncBadge() {
  const { online, pending, syncing, lastSync } = useSync();
  let text, cls;
  if (pending > 0) { text = online ? (syncing ? "Sincronizzo…" : `Salvato sul dispositivo · ${pending} da inviare`) : `Offline · salvato sul dispositivo (${pending})`; cls = "bg-amber-100 text-amber-900 border-amber-300"; }
  else if (!online) { text = "Offline · tutto salvato sul dispositivo"; cls = "bg-slate-100 text-slate-800 border-slate-300"; }
  else { text = lastSync ? "Sincronizzato" : "Connesso"; cls = "bg-emerald-100 text-emerald-900 border-emerald-300"; }
  return <span className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold ${cls}`} role="status" data-testid="sync-badge">{text}</span>;
}
